// src/components/GoogleMap/LocationMap.tsx
import React, { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  GoogleMap,
  DirectionsRenderer,
  InfoWindow,
} from "@react-google-maps/api";
import { useGoogleMaps } from "~/contexts/GoogleMapsContext";
import {
  FaMapMarkerAlt,
  FaDirections,
  FaTimes,
  FaRoute,
  FaCompass,
  FaPlay,
  FaLocationArrow,
  FaUser,
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

// Libraries are now loaded by GoogleMapsProvider at app level

// Review interface for Google Places reviews
interface PlaceReview {
  authorName: string;
  authorUri?: string;
  rating: number;
  text: string;
  relativeTimeDescription?: string;
  profilePhotoUrl?: string;
}

interface LocationMapProps {
  restaurantLocation?: {
    coordinates: string;
    google_maps_link: string;
  } | null;
  userLocation?: string | null; // URL like "https://maps.google.com/?q=lat,lng"
  restaurantName?: string;
  restaurantLogo?: string | null; // Add logo prop for restaurant marker
  placeId?: string | null; // Google Places ID for fetching reviews
  theme?: {
    primaryColor: string;
    textColor: string;
    backgroundColor: string;
  };
  className?: string;
  isPickupMode?: boolean;
  onGetUserLocation?: () => void;
  showReviews?: boolean; // Whether to show reviews section
}

interface LatLng {
  lat: number;
  lng: number;
}

const LocationMap: React.FC<LocationMapProps> = ({
  restaurantLocation,
  userLocation,
  restaurantName = "Restaurant",
  restaurantLogo,
  placeId,
  theme,
  className = "",
  isPickupMode = false,
  onGetUserLocation,
  showReviews = false,
}) => {
  const { t, i18n } = useTranslation();

  // Use the Google Maps context instead of local script loading
  const { isLoaded: isGoogleMapsLoaded, loadError: googleMapsLoadError } = useGoogleMaps();

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const [showDirections, setShowDirections] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState<
    "restaurant" | "user" | null
  >(null);


  // Reviews state
  const [reviews, setReviews] = useState<PlaceReview[]>([]);
  const [placeDetails, setPlaceDetails] = useState<{
    name?: string;
    address?: string;
    rating?: number;
    totalRatings?: number;
  } | null>(null);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [showReviewsPanel, setShowReviewsPanel] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showNavigationOverlay, setShowNavigationOverlay] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Advanced marker refs
  const restaurantMarkerRef = React.useRef<
    google.maps.marker.AdvancedMarkerElement | google.maps.Marker | null
  >(null);
  const userMarkerRef = React.useRef<
    google.maps.marker.AdvancedMarkerElement | google.maps.Marker | null
  >(null);

  const detachMarker = (
    marker: google.maps.marker.AdvancedMarkerElement | google.maps.Marker | null
  ) => {
    if (!marker) return;
    if (typeof (marker as google.maps.Marker).setMap === "function") {
      (marker as google.maps.Marker).setMap(null);
      return;
    }
    (marker as google.maps.marker.AdvancedMarkerElement).map = null;
  };

  // Create custom marker content for user
  const createUserMarkerContent = (): HTMLElement => {
    const container = document.createElement("div");
    container.innerHTML = `
      <div style="
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: #3B82F6;
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      ">
        <svg width="20" height="20" viewBox="0 0 448 512" fill="white">
          <path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z"/>
        </svg>
      </div>
    `;
    return container.firstElementChild as HTMLElement;
  };

  // Create custom marker content for restaurant
  const createRestaurantMarkerContent = (
    logoUrl?: string | null
  ): HTMLElement => {
    const container = document.createElement("div");
    const primaryColor = theme?.primaryColor || "#ea580c";

    if (logoUrl) {
      container.innerHTML = `
        <div style="
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: ${primaryColor};
          padding: 3px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          cursor: pointer;
        ">
          <img 
            src="${logoUrl}" 
            alt="Restaurant"
            style="
              width: 100%;
              height: 100%;
              border-radius: 50%;
              object-fit: cover;
              background: white;
            "
            onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
          />
          <div style="
            display: none;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            background: white;
            align-items: center;
            justify-content: center;
            font-size: 20px;
          ">🏪</div>
        </div>
      `;
    } else {
      container.innerHTML = `
        <div style="
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: ${primaryColor};
          padding: 3px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        ">
          <div style="
            width: 100%;
            height: 100%;
            border-radius: 50%;
            background: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
          ">🏪</div>
        </div>
      `;
    }
    return container.firstElementChild as HTMLElement;
  };

  // Parse coordinates from different formats
  const parseCoordinates = (coordString: string): LatLng | null => {
    try {
      // Handle "lat,lng" format
      if (coordString.includes(",")) {
        const [lat, lng] = coordString
          .split(",")
          .map((coord) => parseFloat(coord.trim()));
        if (!isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }

      // Handle Google Maps URL format
      const urlMatch = coordString.match(/q=([+-]?\d+\.?\d*),([+-]?\d+\.?\d*)/);
      if (urlMatch) {
        const lat = parseFloat(urlMatch[1]);
        const lng = parseFloat(urlMatch[2]);
        if (!isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }

      return null;
    } catch (error) {
      console.error("Error parsing coordinates:", error);
      return null;
    }
  };

  // Get restaurant coordinates
  const restaurantCoords = restaurantLocation?.coordinates
    ? parseCoordinates(restaurantLocation.coordinates)
    : null;

  // Get user coordinates
  const userCoords = userLocation ? parseCoordinates(userLocation) : null;

  // Calculate center and zoom based on available locations
  const getMapCenter = (): LatLng => {
    if (restaurantCoords && userCoords) {
      // Center between both points
      return {
        lat: (restaurantCoords.lat + userCoords.lat) / 2,
        lng: (restaurantCoords.lng + userCoords.lng) / 2,
      };
    }

    if (restaurantCoords) return restaurantCoords;
    if (userCoords) return userCoords;

    // Default to a general location if no coordinates available
    return { lat: 40.7128, lng: -74.006 }; // New York City
  };

  const getZoomLevel = (): number => {
    if (restaurantCoords && userCoords) {
      // Calculate distance and adjust zoom accordingly
      const latDiff = Math.abs(restaurantCoords.lat - userCoords.lat);
      const lngDiff = Math.abs(restaurantCoords.lng - userCoords.lng);
      const maxDiff = Math.max(latDiff, lngDiff);

      if (maxDiff > 1) return 8;
      if (maxDiff > 0.1) return 11;
      if (maxDiff > 0.01) return 13;
      return 15;
    }

    return 13; // Default zoom for single location
  };

  const mapContainerStyle = {
    width: "100%",
    height: "300px",
    borderRadius: "12px",
  };

  const mapOptions: google.maps.MapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    mapId: "DEMO_MAP_ID", // Required for AdvancedMarkerElement - replace with your Map ID from Google Cloud Console
  };

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
    setIsLoaded(true);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
    setIsLoaded(false);
  }, []);

  // Create AdvancedMarkerElement instances when map is loaded
  useEffect(() => {
    if (!isLoaded || !map) return;
    if (typeof window === "undefined" || !window.google || !window.google.maps)
      return;

    const createMarkers = async () => {
      try {
        let AdvancedMarkerCtor:
          | typeof google.maps.marker.AdvancedMarkerElement
          | null = null;

        if (typeof google.maps.importLibrary === "function") {
          const { AdvancedMarkerElement } = (await google.maps.importLibrary(
            "marker"
          )) as google.maps.MarkerLibrary;
          AdvancedMarkerCtor = AdvancedMarkerElement;
        } else if (google.maps.marker?.AdvancedMarkerElement) {
          AdvancedMarkerCtor = google.maps.marker.AdvancedMarkerElement;
        }

        // Create restaurant marker
        if (restaurantCoords) {
          if (restaurantMarkerRef.current) {
            detachMarker(restaurantMarkerRef.current);
            restaurantMarkerRef.current = null;
          }

          if (AdvancedMarkerCtor) {
            const restaurantContent =
              createRestaurantMarkerContent(restaurantLogo);
            restaurantMarkerRef.current = new AdvancedMarkerCtor({
              map,
              position: restaurantCoords,
              title: restaurantName,
              content: restaurantContent,
            });
          } else {
            restaurantMarkerRef.current = new google.maps.Marker({
              map,
              position: restaurantCoords,
              title: restaurantName,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: theme?.primaryColor || "#ea580c",
                fillOpacity: 1,
                scale: 10,
                strokeColor: "#ffffff",
                strokeOpacity: 1,
                strokeWeight: 2,
              },
            });
          }

          restaurantMarkerRef.current?.addListener("gmp-click", () => {
            setSelectedMarker((prev) =>
              prev === "restaurant" ? null : "restaurant"
            );
          });
        }

        // Create user marker
        if (userCoords) {
          if (userMarkerRef.current) {
            detachMarker(userMarkerRef.current);
            userMarkerRef.current = null;
          }

          if (AdvancedMarkerCtor) {
            const userContent = createUserMarkerContent();
            userMarkerRef.current = new AdvancedMarkerCtor({
              map,
              position: userCoords,
              title: "Your Location",
              content: userContent,
            });
          } else {
            userMarkerRef.current = new google.maps.Marker({
              map,
              position: userCoords,
              title: "Your Location",
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: "#3B82F6",
                fillOpacity: 1,
                scale: 8,
                strokeColor: "#ffffff",
                strokeOpacity: 1,
                strokeWeight: 2,
              },
            });
          }

          userMarkerRef.current?.addListener("gmp-click", () => {
            setSelectedMarker((prev) => (prev === "user" ? null : "user"));
          });
        }
      } catch (error) {
        console.error("Error creating advanced markers:", error);
      }
    };

    createMarkers();

    // Cleanup function
    return () => {
      if (restaurantMarkerRef.current) {
        detachMarker(restaurantMarkerRef.current);
        restaurantMarkerRef.current = null;
      }
      if (userMarkerRef.current) {
        detachMarker(userMarkerRef.current);
        userMarkerRef.current = null;
      }
    };
  }, [
    isLoaded,
    map,
    restaurantCoords,
    userCoords,
    restaurantLogo,
    restaurantName,
    theme,
  ]);

  // Fetch place reviews when placeId is provided and map is loaded
  useEffect(() => {
    if (!placeId || !isLoaded || !showReviews || !map) return;
    if (
      typeof window === "undefined" ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.places
    )
      return;

    const fetchPlaceReviews = async () => {
      setIsLoadingReviews(true);
      try {
        setReviews([]);

        const modernFields = [
          "displayName",
          "formattedAddress",
          "location",
          "reviews",
          "rating",
          "userRatingCount",
        ];

        if (typeof google.maps.importLibrary === "function") {
          const { Place } = (await google.maps.importLibrary(
            "places"
          )) as google.maps.PlacesLibrary;

          const place = new Place({
            id: placeId,
          });

          await place.fetchFields({ fields: modernFields });

          setPlaceDetails({
            name: place.displayName || restaurantName,
            address: place.formattedAddress || "",
            rating: place.rating || 0,
            totalRatings: place.userRatingCount || 0,
          });

          if (place.reviews && place.reviews.length > 0) {
            const processedReviews: PlaceReview[] = place.reviews.map(
              (review: any) => ({
                authorName:
                  review.authorAttribution?.displayName || "Anonymous",
                authorUri: review.authorAttribution?.uri || "",
                rating: review.rating || 0,
                text: review.text || "",
                relativeTimeDescription:
                  review.relativePublishTimeDescription || "",
                profilePhotoUrl: review.authorAttribution?.photoUri || "",
              })
            );
            setReviews(processedReviews);
          }
          return;
        }

        if (google.maps.places?.PlacesService) {
          // Fallback to legacy PlacesService when importLibrary is unavailable.
          const service = new google.maps.places.PlacesService(
            document.createElement("div")
          );

          const result = await new Promise<google.maps.places.PlaceResult>(
            (resolve, reject) => {
              service.getDetails(
                {
                  placeId,
                  fields: [
                    "name",
                    "formatted_address",
                    "geometry",
                    "reviews",
                    "rating",
                    "user_ratings_total",
                  ],
                },
                (placeResult, status) => {
                  if (
                    status === google.maps.places.PlacesServiceStatus.OK &&
                    placeResult
                  ) {
                    resolve(placeResult);
                  } else {
                    reject(
                      new Error(
                        `PlacesServiceStatus: ${status?.toString() ?? "UNKNOWN"
                        }`
                      )
                    );
                  }
                }
              );
            }
          );

          setPlaceDetails({
            name: result.name || restaurantName,
            address: result.formatted_address || "",
            rating: result.rating || 0,
            totalRatings: result.user_ratings_total || 0,
          });

          if (result.reviews && result.reviews.length > 0) {
            const processedReviews: PlaceReview[] = result.reviews.map(
              (review) => ({
                authorName: review.author_name || "Anonymous",
                authorUri: review.author_url || "",
                rating: review.rating || 0,
                text: review.text || "",
                relativeTimeDescription: review.relative_time_description || "",
                profilePhotoUrl: review.profile_photo_url || "",
              })
            );
            setReviews(processedReviews);
          }
          return;
        }

        throw new Error("Google Maps Places library is unavailable");
      } catch (error) {
        console.error("Error fetching place reviews:", error);
      } finally {
        setIsLoadingReviews(false);
      }
    };

    fetchPlaceReviews();
  }, [placeId, isLoaded, showReviews, map, restaurantName]);

  // Render star rating
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<FaStar key={i} className="text-yellow-400" size={14} />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <FaStarHalfAlt key={i} className="text-yellow-400" size={14} />
        );
      } else {
        stars.push(<FaRegStar key={i} className="text-yellow-400" size={14} />);
      }
    }
    return stars;
  };

  // Open external navigation app (Google Maps, Apple Maps, etc.) - prioritize apps
  const openExternalNavigation = () => {
    if (!restaurantCoords) return;

    const { lat, lng } = restaurantCoords;

    // Detect if it's iOS or Android for better app linking
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isIOS) {
      // Try Apple Maps first
      const appleUrl = `maps://maps.apple.com/?daddr=${lat},${lng}`;
      const googleAppUrl = `comgooglemaps://?daddr=${lat},${lng}`;
      const fallbackUrl = `https://maps.google.com/maps?daddr=${lat},${lng}`;

      // Try Apple Maps first
      window.location.href = appleUrl;

      // If Apple Maps fails, try Google Maps app after a short delay
      setTimeout(() => {
        try {
          window.location.href = googleAppUrl;
          // Final fallback to web if both apps fail
          setTimeout(() => {
            window.open(fallbackUrl, "_blank");
          }, 1000);
        } catch {
          window.open(fallbackUrl, "_blank");
        }
      }, 1000);
    } else if (isAndroid) {
      // Try Google Maps app first on Android
      const googleAppUrl = `google.navigation:q=${lat},${lng}`;
      const fallbackUrl = `https://maps.google.com/maps?daddr=${lat},${lng}`;

      try {
        window.location.href = googleAppUrl;
        // Fallback to web if app fails
        setTimeout(() => {
          window.open(fallbackUrl, "_blank");
        }, 1000);
      } catch {
        window.open(fallbackUrl, "_blank");
      }
    } else {
      // Desktop - open Google Maps web
      const navigationUrl = `https://maps.google.com/maps?daddr=${lat},${lng}`;
      window.open(navigationUrl, "_blank");
    }
  };

  // Navigate with user location using apps (for "Get My Location" flow)
  const navigateWithUserLocationToApp = () => {
    if (!restaurantCoords) return;

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          const { lat: destLat, lng: destLng } = restaurantCoords;

          // Detect platform and use appropriate navigation app
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const isAndroid = /Android/.test(navigator.userAgent);

          if (isIOS) {
            // Try Apple Maps with source and destination
            const appleUrl = `maps://maps.apple.com/?saddr=${userLat},${userLng}&daddr=${destLat},${destLng}`;
            const googleAppUrl = `comgooglemaps://?saddr=${userLat},${userLng}&daddr=${destLat},${destLng}`;
            const fallbackUrl = `https://maps.google.com/maps?saddr=${userLat},${userLng}&daddr=${destLat},${destLng}`;

            window.location.href = appleUrl;
            setTimeout(() => {
              try {
                window.location.href = googleAppUrl;
                setTimeout(() => {
                  window.open(fallbackUrl, "_blank");
                }, 1000);
              } catch {
                window.open(fallbackUrl, "_blank");
              }
            }, 1000);
          } else if (isAndroid) {
            // Use Google Navigation app for Android
            const googleAppUrl = `google.navigation:q=${destLat},${destLng}`;
            const fallbackUrl = `https://maps.google.com/maps?saddr=${userLat},${userLng}&daddr=${destLat},${destLng}`;

            try {
              window.location.href = googleAppUrl;
              setTimeout(() => {
                window.open(fallbackUrl, "_blank");
              }, 1000);
            } catch {
              window.open(fallbackUrl, "_blank");
            }
          } else {
            // Desktop - open Google Maps web with route
            const navigationUrl = `https://maps.google.com/maps?saddr=${userLat},${userLng}&daddr=${destLat},${destLng}`;
            window.open(navigationUrl, "_blank");
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          // If location fails, fall back to basic app navigation
          openExternalNavigation();
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      // Geolocation not available, fallback to basic navigation
      openExternalNavigation();
    }
  };

  // Start interactive navigation within the app
  const startInteractiveNavigation = () => {
    if (!restaurantCoords) return;

    setIsNavigating(true);
    setShowNavigationOverlay(true);

    // Get user's current location for navigation
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;

          // Create route using Google Directions API
          if (map && isLoaded) {
            const directionsService = new google.maps.DirectionsService();

            directionsService.route(
              {
                origin: { lat: userLat, lng: userLng },
                destination: restaurantCoords,
                travelMode: google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === "OK" && result) {
                  setDirections(result);
                  setShowDirections(true);
                  setIsNavigating(false);

                  // Focus the map on the route
                  const bounds = new google.maps.LatLngBounds();
                  result.routes[0].legs.forEach((leg) => {
                    bounds.extend(leg.start_location);
                    bounds.extend(leg.end_location);
                  });
                  // Add padding at bottom to account for compact navigation bar
                  map.fitBounds(bounds, {
                    top: 20,
                    right: 20,
                    bottom: 60,
                    left: 20,
                  });
                } else {
                  console.error("Directions request failed:", status);
                  setIsNavigating(false);
                  // Fallback to external navigation
                  navigateWithUserLocationToApp();
                }
              }
            );
          } else {
            // If map not loaded, fallback to external navigation
            navigateWithUserLocationToApp();
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          setIsNavigating(false);
          // Fallback to external navigation without user location
          openExternalNavigation();
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setIsNavigating(false);
      // Fallback if geolocation not available
      openExternalNavigation();
    }
  };

  // Hide navigation overlay but keep the route visible
  const hideNavigationOverlay = () => {
    setShowNavigationOverlay(false);
  };

  if (!restaurantCoords && !userCoords) {
    return (
      <div
        className={`p-4 text-center rounded-lg ${className}`}
        style={{ backgroundColor: `${theme?.primaryColor || "#ea580c"}10` }}
      >
        <FaMapMarkerAlt
          className="mx-auto mb-2"
          size={24}
          style={{ color: theme?.primaryColor || "#ea580c" }}
        />
        <p style={{ color: theme?.textColor || "#6b7280" }}>
          {t("menu.cart.location.locationNotAvailable")}
        </p>
      </div>
    );
  }

  // Show message if no API key
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return (
      <div
        className={`p-4 text-center rounded-lg border ${className}`}
        style={{
          backgroundColor: `${theme?.primaryColor || "#ea580c"}10`,
          borderColor: theme?.primaryColor || "#ea580c",
        }}
      >
        <FaMapMarkerAlt
          className="mx-auto mb-2"
          size={24}
          style={{ color: theme?.primaryColor || "#ea580c" }}
        />
        <p style={{ color: theme?.textColor || "#6b7280" }} className="text-sm">
          {t("menu.cart.location.mapRequiresApiKey")}
        </p>
      </div>
    );
  }

  // If script fails to load (blocked, invalid key, network), show fallback instead of loading forever.
  if (googleMapsLoadError) {
    return (
      <div
        className={`p-4 text-center rounded-lg border ${className}`}
        style={{
          backgroundColor: `${theme?.primaryColor || "#ea580c"}10`,
          borderColor: theme?.primaryColor || "#ea580c",
        }}
      >
        <FaMapMarkerAlt
          className="mx-auto mb-2"
          size={24}
          style={{ color: theme?.primaryColor || "#ea580c" }}
        />
        <p style={{ color: theme?.textColor || "#6b7280" }} className="text-sm">
          {googleMapsLoadError}
        </p>
        <button
          type="button"
          onClick={openExternalNavigation}
          className="mt-3 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90"
          style={{ backgroundColor: theme?.primaryColor || "#ea580c" }}
        >
          {t("openInMaps", { defaultValue: "Open in Maps" })}
        </button>
      </div>
    );
  }

  // Show loading state while Google Maps is loading
  if (!isGoogleMapsLoaded) {
    return (
      <div
        className={`p-4 text-center rounded-lg ${className}`}
        style={{ backgroundColor: `${theme?.primaryColor || "#ea580c"}10` }}
      >
        <p style={{ color: theme?.textColor || "#6b7280" }} className="text-sm">
          {t("menu.cart.location.loadingMap", {
            defaultValue: "Loading map…",
          })}
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="space-y-3">
        {/* Action Buttons */}
        {isPickupMode && restaurantCoords && (
          /* Pickup Mode Navigation Buttons */
          <div className="flex flex-col gap-2">
            <button
              onClick={startInteractiveNavigation}
              disabled={isNavigating}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-medium transition-all hover:opacity-90 shadow-md disabled:opacity-50"
              style={{ backgroundColor: theme?.primaryColor || "#ea580c" }}
            >
              {isNavigating ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    <FaCompass size={14} />
                  </motion.div>
                  <span>{t("menu.cart.location.gettingRoute")}</span>
                </>
              ) : (
                <>
                  <FaDirections size={14} />
                  <span>{t("menu.cart.location.navigateHere")}</span>
                </>
              )}
            </button>

            {onGetUserLocation && !userLocation && (
              <button
                onClick={onGetUserLocation}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 hover:bg-opacity-10"
                style={{
                  borderColor: theme?.primaryColor || "#ea580c",
                  color: theme?.primaryColor || "#ea580c",
                  backgroundColor: `${theme?.primaryColor || "#ea580c"}08`,
                }}
              >
                <FaLocationArrow size={12} />
                {t("menu.cart.location.getMyLocation")}
              </button>
            )}

            {userLocation && (
              <button
                onClick={navigateWithUserLocationToApp}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 hover:bg-opacity-10"
                style={{
                  borderColor: "#10b981",
                  color: "#10b981",
                  backgroundColor: "#10b98108",
                }}
              >
                <FaRoute size={12} />
                {t("menu.cart.location.navigateFromMyLocation")}
              </button>
            )}
          </div>
        )}

        {/* Map */}
        <div className="relative">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={getMapCenter()}
            zoom={getZoomLevel()}
            options={mapOptions}
            onLoad={onLoad}
            onUnmount={onUnmount}
          >
            {/* Markers are now created via useEffect with AdvancedMarkerElement */}

            {/* Info Windows */}
            {selectedMarker === "restaurant" && restaurantCoords && (
              <InfoWindow
                key="restaurant-info"
                position={restaurantCoords}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div className="p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <FaMapMarkerAlt
                      size={16}
                      style={{ color: theme?.primaryColor || "#ea580c" }}
                    />
                    <h4 className="font-semibold">{restaurantName}</h4>
                  </div>
                  <p className="text-sm text-gray-600">Restaurant Location</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      startInteractiveNavigation();
                      setSelectedMarker(null);
                    }}
                    className="mt-2 px-3 py-1.5 rounded-lg text-white text-xs font-medium transition-all hover:opacity-90"
                    style={{
                      backgroundColor: theme?.primaryColor || "#ea580c",
                    }}
                  >
                    <FaPlay size={10} className="inline mr-1" />
                    {t("menu.cart.location.startNavigation")}
                  </button>
                </div>
              </InfoWindow>
            )}

            {selectedMarker === "user" && userCoords && (
              <InfoWindow
                key="user-info"
                position={userCoords}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div className="p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <FaUser size={16} className="text-green-500" />
                    <h4 className="font-semibold">
                      {t("menu.cart.location.yourLocation")}
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    {t("menu.deliveryAddress")}
                  </p>
                </div>
              </InfoWindow>
            )}

            {/* Directions */}
            {directions && showDirections && (
              <DirectionsRenderer
                key="directions-renderer"
                directions={directions}
                options={{
                  polylineOptions: {
                    strokeColor: theme?.primaryColor || "#ea580c",
                    strokeWeight: 4,
                    strokeOpacity: 0.8,
                  },
                  suppressMarkers: true, // We're using custom markers
                }}
              />
            )}
          </GoogleMap>

          {/* Compact Navigation Bar - shows distance/time inline */}
          {showNavigationOverlay && directions && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute bottom-2 left-2 right-2 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border px-3 py-2 flex items-center justify-between gap-2"
              style={{ borderColor: theme?.primaryColor || "#ea580c" }}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <FaRoute
                    size={12}
                    style={{ color: theme?.primaryColor || "#ea580c" }}
                  />
                  <span
                    className="text-xs font-semibold"
                    style={{ color: theme?.textColor || "#1f2937" }}
                  >
                    {directions.routes[0]?.legs[0]?.distance?.text}
                  </span>
                </div>
                <span className="text-gray-300">|</span>
                <span
                  className="text-xs font-semibold"
                  style={{ color: theme?.textColor || "#1f2937" }}
                >
                  {directions.routes[0]?.legs[0]?.duration?.text}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={navigateWithUserLocationToApp}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-white text-xs font-medium transition-all hover:opacity-90"
                  style={{
                    backgroundColor: theme?.primaryColor || "#ea580c",
                  }}
                >
                  <FaDirections size={10} />
                  {t("openInMaps")}
                </button>
                <button
                  onClick={hideNavigationOverlay}
                  className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  title={t("menu.cart.location.hideOverlay", {
                    defaultValue: "Hide",
                  })}
                >
                  <FaTimes
                    size={12}
                    style={{ color: theme?.textColor || "#6b7280" }}
                  />
                </button>
              </div>
            </motion.div>
          )}

          {/* Floating Navigation Button - appears when overlay is hidden but route is active */}
          {isPickupMode &&
            restaurantCoords &&
            isLoaded &&
            !showNavigationOverlay &&
            !isNavigating &&
            directions && (
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowNavigationOverlay(true)}
                className="absolute bottom-4 right-4 w-12 h-12 rounded-full shadow-2xl flex items-center justify-center text-white transition-all hover:shadow-xl"
                style={{
                  backgroundColor: theme?.primaryColor || "#ea580c",
                  boxShadow: `0 4px 20px ${theme?.primaryColor || "#ea580c"
                    }40`,
                }}
                title={t("menu.cart.location.showRouteInfo", {
                  defaultValue: "Show route info",
                })}
              >
                <FaRoute size={14} />
              </motion.button>
            )}

          {/* Floating Start Navigation Button - appears when no route is active */}
          {isPickupMode &&
            restaurantCoords &&
            isLoaded &&
            !showNavigationOverlay &&
            !isNavigating &&
            !directions && (
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={startInteractiveNavigation}
                className="absolute bottom-4 right-4 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white transition-all hover:shadow-xl"
                style={{
                  backgroundColor: theme?.primaryColor || "#ea580c",
                  boxShadow: `0 4px 20px ${theme?.primaryColor || "#ea580c"
                    }40`,
                }}
              >
                <FaPlay size={16} />
              </motion.button>
            )}
        </div>

        {/* Distance Info */}
        {directions && showDirections && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-2 rounded-lg text-center text-sm"
            style={{
              backgroundColor: `${theme?.primaryColor || "#ea580c"}10`,
              color: theme?.textColor || "#374151",
            }}
          >
            <div className="flex justify-center gap-4">
              <span>
                <strong>{t("menu.cart.location.distance")}:</strong>{" "}
                {directions.routes[0]?.legs[0]?.distance?.text}
              </span>
              <span>
                <strong>{t("menu.cart.location.time")}:</strong>{" "}
                {directions.routes[0]?.legs[0]?.duration?.text}
              </span>
            </div>
          </motion.div>
        )}

        {/* Reviews Section */}
        {showReviews && placeId && (
          <div className="mt-4">
            {/* Reviews Toggle Button */}
            <motion.button
              onClick={() => setShowReviewsPanel(!showReviewsPanel)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full p-3 rounded-lg border flex items-center justify-between transition-all"
              style={{
                backgroundColor: showReviewsPanel
                  ? `${theme?.primaryColor || "#ea580c"}10`
                  : theme?.backgroundColor || "#ffffff",
                borderColor: theme?.primaryColor || "#ea580c",
              }}
            >
              <div className="flex items-center gap-3">
                <FaStar
                  size={16}
                  style={{ color: theme?.primaryColor || "#ea580c" }}
                />
                <span
                  style={{ color: theme?.textColor || "#1f2937" }}
                  className="font-medium"
                >
                  {t("menu.reviews.title", { defaultValue: "Reviews" })}
                </span>
                {placeDetails?.rating && (
                  <span className="flex items-center gap-1">
                    <span
                      className="font-bold"
                      style={{ color: theme?.primaryColor || "#ea580c" }}
                    >
                      {placeDetails.rating.toFixed(1)}
                    </span>
                    <span
                      className="text-sm"
                      style={{ color: theme?.textColor || "#6b7280" }}
                    >
                      ({placeDetails.totalRatings})
                    </span>
                  </span>
                )}
              </div>
              <motion.div
                animate={{ rotate: showReviewsPanel ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <FaTimes
                  size={12}
                  style={{
                    color: theme?.textColor || "#6b7280",
                    transform: "rotate(45deg)",
                  }}
                />
              </motion.div>
            </motion.button>

            {/* Reviews Panel */}
            <AnimatePresence>
              {showReviewsPanel && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div
                    className="mt-2 p-4 rounded-lg border"
                    style={{
                      backgroundColor: theme?.backgroundColor || "#ffffff",
                      borderColor: `${theme?.primaryColor || "#ea580c"}30`,
                    }}
                  >
                    {isLoadingReviews ? (
                      <div className="flex items-center justify-center py-8">
                        <div
                          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
                          style={{
                            borderColor: theme?.primaryColor || "#ea580c",
                          }}
                        />
                      </div>
                    ) : reviews.length > 0 ? (
                      <div className="space-y-4 max-h-[400px] overflow-y-auto">
                        {/* Overall Rating */}
                        {placeDetails && (
                          <div
                            className="p-4 rounded-lg mb-4"
                            style={{
                              backgroundColor: `${theme?.primaryColor || "#ea580c"
                                }10`,
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <span
                                className="text-3xl font-bold"
                                style={{
                                  color: theme?.primaryColor || "#ea580c",
                                }}
                              >
                                {placeDetails.rating?.toFixed(1)}
                              </span>
                              <div>
                                <div className="flex items-center gap-1">
                                  {renderStars(placeDetails.rating || 0)}
                                </div>
                                <p
                                  className="text-sm mt-1"
                                  style={{
                                    color: theme?.textColor || "#6b7280",
                                  }}
                                >
                                  {t("menu.reviews.basedOn", {
                                    defaultValue:
                                      "Based on {{count}} reviews",
                                    count: placeDetails.totalRatings,
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Individual Reviews */}
                        {reviews.map((review, index) => (
                          <motion.div
                            key={`review-${index}-${review.authorName}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="p-4 rounded-lg border"
                            style={{
                              backgroundColor:
                                theme?.backgroundColor || "#ffffff",
                              borderColor: `${theme?.primaryColor || "#ea580c"
                                }30`,
                            }}
                          >
                            {/* Review Header */}
                            <div className="flex items-start gap-3 mb-3">
                              {review.profilePhotoUrl ? (
                                <img
                                  src={review.profilePhotoUrl}
                                  alt={review.authorName}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div
                                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                                  style={{
                                    backgroundColor:
                                      theme?.primaryColor || "#ea580c",
                                  }}
                                >
                                  {review.authorName.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  {review.authorUri ? (
                                    <a
                                      href={review.authorUri}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="font-semibold hover:underline"
                                      style={{
                                        color: theme?.textColor || "#1f2937",
                                      }}
                                    >
                                      {review.authorName}
                                    </a>
                                  ) : (
                                    <span
                                      className="font-semibold"
                                      style={{
                                        color: theme?.textColor || "#1f2937",
                                      }}
                                    >
                                      {review.authorName}
                                    </span>
                                  )}
                                  {review.relativeTimeDescription && (
                                    <span
                                      className="text-xs"
                                      style={{
                                        color: theme?.textColor || "#6b7280",
                                      }}
                                    >
                                      {review.relativeTimeDescription}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  {renderStars(review.rating)}
                                </div>
                              </div>
                            </div>

                            {/* Review Text */}
                            {review.text && (
                              <p
                                className="text-sm leading-relaxed"
                                style={{
                                  color: theme?.textColor || "#4b5563",
                                }}
                                dir={i18n.language === "ar" ? "rtl" : "ltr"}
                              >
                                {review.text}
                              </p>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div
                        className="text-center py-8"
                        style={{ color: theme?.textColor || "#6b7280" }}
                      >
                        <FaStar
                          size={32}
                          className="mx-auto mb-3 opacity-30"
                        />
                        <p>
                          {t("menu.reviews.noReviews", {
                            defaultValue: "No reviews available",
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationMap;
